/**
 * feat-305 — client for the manager assistant (ADR 0001 §1, §6.5, §7, §10.2–§10.3).
 *
 *   GET  /api/assistant/config?tenant_id=…          → { enabled, pages }
 *   POST /api/assistant/conversations/current        → { conversation | null }
 *   POST /api/assistant/chat                         → text/event-stream (SSE)
 *
 * Every function short-circuits to the in-memory fake responder in `start:fake`
 * (`config.environment === 'fake'`, the serverScorecardService pattern;
 * `config.useDummyData` is false everywhere and is NOT consulted).
 *
 * `streamChat` resolves after a terminal event (`done` or in-stream `error`,
 * both delivered through `onEvent`) or after the caller aborts. It REJECTS only
 * with an `AssistantError`: pre-stream HTTP failures, a dead stream (45 s with
 * no bytes; the server pings every 15 s) and a stream that ends without a
 * terminal event all reject. The panel owns the user-facing copy per code; the
 * `message` here is a fallback only.
 */
import { config } from '../config/environment';
import { authedApiFetch } from './tokenRefresh';
import { createSseParser, toAssistantEvent } from '../components/assistant/sseParser';
import type {
  AssistantConfig,
  AssistantConversation,
  AssistantError,
  AssistantErrorCode,
  AssistantEvent,
  ChatRequest,
  PageContext,
} from '../components/assistant/types';
import {
  fakeGetAssistantConfig,
  fakeGetCurrentConversation,
  fakeStreamChat,
} from './fakeAssistantData';

/** No bytes for this long → the stream is dead (ADR §1.2 / §10.3). */
export const ASSISTANT_WATCHDOG_MS = 45_000;

const isFake = (): boolean => config.environment === 'fake';

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

export function assistantError(
  code: AssistantErrorCode,
  message = '',
  retryable = true,
  retryAfterS: number | null = null,
): AssistantError {
  return { code, message, retryable, retryAfterS };
}

const networkError = () =>
  assistantError('network', 'The connection to the assistant was lost.', true, null);

/** Map a non-2xx response to an `AssistantError` (ADR §10.3). Never throws. */
export async function normalizeHttpError(resp: Response): Promise<AssistantError> {
  let body: unknown = null;
  try {
    body = await resp.json();
  } catch {
    body = null;
  }
  const detail =
    body && typeof body === 'object' ? (body as { detail?: unknown }).detail : undefined;
  switch (resp.status) {
    case 401:
    case 403:
      return assistantError('unauthorized', 'You are not signed in.', false);
    case 404:
      return assistantError('not_found', 'Not found', false);
    case 422:
      return assistantError('invalid', 'The request was not valid.', false);
    case 429: {
      if (detail && typeof detail === 'object') {
        const d = detail as Record<string, unknown>;
        const code = d.code;
        if (code === 'limit_rate' || code === 'limit_busy' || code === 'limit_conversation') {
          return assistantError(
            code,
            typeof d.message === 'string' ? d.message : '',
            typeof d.retryable === 'boolean' ? d.retryable : code !== 'limit_conversation',
            typeof d.retry_after_s === 'number' ? d.retry_after_s : null,
          );
        }
      }
      return assistantError('limit_rate', '', true);
    }
    default:
      return assistantError('upstream', `HTTP ${resp.status}`, true);
  }
}

// ── config (cached per tenant for the session) ──────────────────────────────

const configCache = new Map<string, Promise<AssistantConfig>>();

/** Test hook: forget cached config. */
export function __resetAssistantConfigCache(): void {
  configCache.clear();
}

/**
 * Forget the cached config for one tenant, so the next `getAssistantConfig`
 * re-fetches. Called after an admin flips the tenant's `assistant_enabled`
 * flag in tenant management, so the launcher reflects it without a reload.
 */
export function invalidateAssistantConfig(tenantId: string): void {
  configCache.delete(tenantId);
}

export function getAssistantConfig(tenantId: string): Promise<AssistantConfig> {
  if (isFake()) return fakeGetAssistantConfig(tenantId);
  const cached = configCache.get(tenantId);
  if (cached) return cached;
  const p = (async () => {
    const resp = await authedApiFetch(
      backendUrl(`/api/assistant/config?tenant_id=${encodeURIComponent(tenantId)}`),
      { method: 'GET', headers: { 'Content-Type': 'application/json' } },
    );
    if (!resp.ok) throw await normalizeHttpError(resp);
    const json = (await resp.json()) as Partial<AssistantConfig>;
    return {
      enabled: json.enabled === true,
      pages: Array.isArray(json.pages) ? json.pages.filter((p) => typeof p === 'string') : [],
    };
  })();
  configCache.set(tenantId, p);
  // A failure is not cached: the next mount may try again.
  p.catch(() => configCache.delete(tenantId));
  return p;
}

// ── restore ──────────────────────────────────────────────────────────────────

export async function getCurrentConversation(
  ctx: PageContext,
): Promise<AssistantConversation | null> {
  if (isFake()) return fakeGetCurrentConversation(ctx);
  let resp: Response;
  try {
    resp = await authedApiFetch(backendUrl('/api/assistant/conversations/current'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page_context: ctx }),
    });
  } catch {
    throw networkError();
  }
  if (!resp.ok) throw await normalizeHttpError(resp);
  const json = (await resp.json()) as { conversation?: AssistantConversation | null };
  return json.conversation ?? null;
}

// ── chat stream ──────────────────────────────────────────────────────────────

const isTerminal = (e: AssistantEvent) => e.type === 'done' || e.type === 'error';

export async function streamChat(
  req: ChatRequest,
  onEvent: (e: AssistantEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  if (isFake()) return fakeStreamChat(req, onEvent, signal);
  if (signal.aborted) return;

  // One internal controller aborts the fetch for either reason: the caller's
  // Stop, or our watchdog. `watchdogFired` tells them apart.
  const controller = new AbortController();
  let watchdogFired = false;
  const onCallerAbort = () => controller.abort();
  signal.addEventListener('abort', onCallerAbort);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const armWatchdog = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      watchdogFired = true;
      controller.abort();
    }, ASSISTANT_WATCHDOG_MS);
  };
  const cleanup = () => {
    if (timer) clearTimeout(timer);
    signal.removeEventListener('abort', onCallerAbort);
  };
  // Abort (Stop) resolves quietly; a watchdog abort or any other failure is `network`.
  const settleFailure = (): void => {
    if (signal.aborted && !watchdogFired) return;
    throw networkError();
  };

  try {
    armWatchdog();
    let resp: Response;
    try {
      resp = await authedApiFetch(backendUrl('/api/assistant/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(req),
        signal: controller.signal,
      });
    } catch {
      return settleFailure();
    }
    if (!resp.ok) {
      throw await normalizeHttpError(resp);
    }
    if (!resp.body) throw networkError();

    const reader = resp.body.getReader();
    const decoder = new TextDecoder('utf-8');
    const parser = createSseParser();
    armWatchdog();
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch {
        return settleFailure();
      }
      if (chunk.done) break;
      armWatchdog();
      const text = decoder.decode(chunk.value, { stream: true });
      for (const parsed of parser.push(text)) {
        const ev = toAssistantEvent(parsed);
        if (!ev) continue;
        onEvent(ev);
        if (isTerminal(ev)) {
          try {
            await reader.cancel();
          } catch {
            /* already closed */
          }
          return;
        }
      }
      if (watchdogFired || signal.aborted) return settleFailure();
    }
    // Ended with no terminal event (a drained task, a dropped connection).
    if (signal.aborted && !watchdogFired) return;
    throw networkError();
  } finally {
    cleanup();
  }
}
